import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  MENU_BAR,
  TEACH_PANEL,
  toHexColor,
} from '../config/gameConfig';
import type { EventBus, Term } from '../systems/EventBus';
import { TEACH_PANELS } from '../content/teach-panels';
import { autoMark } from './autoMark';
import { renderMarkedText } from './MarkedText';
import { propagateDepth } from './propagateDepth';
import type { Tooltip } from './Tooltip';

/**
 * Layer 1 "Why it matters" teach panel.
 * Slides in from the right on `teach:requested`, emits `teach:dismissed`
 * and `term:unlocked` on click/ESC. One overlay at a time; re-requesting
 * the same term while visible is a no-op. Spec §6 Layer 1.
 */
export class TeachPanel {
  private current: Term | null = null;
  private queue: Term[] = [];
  private container: Phaser.GameObjects.Container | null = null;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;
  private readonly onTeachRequested = (p: { term: Term }) => {
    if (this.current) {
      if (this.current !== p.term && !this.queue.includes(p.term)) this.queue.push(p.term);
      return;
    }
    this.show(p.term);
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly bus: EventBus,
    private readonly tooltip?: Tooltip,
  ) {
    bus.on('teach:requested', this.onTeachRequested);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  private show(term: Term): void {
    if (this.current) return;
    const copy = TEACH_PANELS[term];
    this.current = term;

    const targetX = GAME_WIDTH - TEACH_PANEL.width - 16;
    const targetY = Phaser.Math.Clamp(
      MENU_BAR.height + 24,
      MENU_BAR.height,
      GAME_HEIGHT - TEACH_PANEL.height,
    );

    const panel = this.scene.add.container(GAME_WIDTH, targetY);
    panel.setDepth(DEPTHS.teachPanel);

    const bg = this.scene.add
      .rectangle(0, 0, TEACH_PANEL.width, TEACH_PANEL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive(); // Trap clicks so they can't reach windows underneath.
    panel.add(bg);

    const title = this.scene.add.text(
      TEACH_PANEL.paddingX,
      TEACH_PANEL.paddingY,
      copy.title,
      {
        fontFamily: FONTS.ui,
        fontSize: TEACH_PANEL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    panel.add(title);

    const bodyY = TEACH_PANEL.paddingY + title.height + 12;
    const body = this.scene.add.text(TEACH_PANEL.paddingX, bodyY, copy.definition, {
      fontFamily: FONTS.ui,
      fontSize: TEACH_PANEL.bodyFontSize,
      color: toHexColor(COLORS.textPrimary),
      wordWrap: { width: TEACH_PANEL.width - TEACH_PANEL.paddingX * 2, useAdvancedWrap: true },
    });
    panel.add(body);

    const empathyY = bodyY + body.height + 12;
    if (this.tooltip) {
      const segs = autoMark(copy.empathyLine);
      renderMarkedText(
        this.scene,
        this.bus,
        this.tooltip,
        panel,
        TEACH_PANEL.paddingX,
        empathyY,
        segs,
        {
          fontSize: TEACH_PANEL.bodyFontSize,
          color: toHexColor(COLORS.textMuted),
          fontStyle: 'italic',
        },
        { wrapWidth: TEACH_PANEL.width - TEACH_PANEL.paddingX * 2 },
      );
    } else {
      const empathy = this.scene.add.text(TEACH_PANEL.paddingX, empathyY, copy.empathyLine, {
        fontFamily: FONTS.ui,
        fontSize: TEACH_PANEL.bodyFontSize,
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'italic',
        wordWrap: { width: TEACH_PANEL.width - TEACH_PANEL.paddingX * 2, useAdvancedWrap: true },
      });
      panel.add(empathy);
    }

    const buttonY = TEACH_PANEL.height - TEACH_PANEL.dismissButtonHeight - TEACH_PANEL.paddingY;
    const button = this.scene.add
      .rectangle(
        TEACH_PANEL.paddingX,
        buttonY,
        TEACH_PANEL.width - TEACH_PANEL.paddingX * 2,
        TEACH_PANEL.dismissButtonHeight,
        COLORS.buttonPrimary,
      )
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    panel.add(button);

    const buttonText = this.scene.add.text(
      TEACH_PANEL.width / 2,
      buttonY + TEACH_PANEL.dismissButtonHeight / 2,
      TEACH_PANEL.dismissButtonText,
      {
        fontFamily: FONTS.ui,
        fontSize: '14px',
        color: toHexColor(COLORS.buttonPrimaryText),
        fontStyle: 'bold',
      },
    ).setOrigin(0.5);
    panel.add(buttonText);

    button.on('pointerdown', () => this.dismiss());
    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.dismiss();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.scene.tweens.add({
      targets: panel,
      x: targetX,
      duration: 220,
      ease: 'Sine.easeOut',
    });

    this.container = panel;
    propagateDepth(panel, DEPTHS.teachPanel);
  }

  private dismiss(): void {
    if (!this.container || !this.current) return;
    const term = this.current;
    const panel = this.container;
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    this.scene.tweens.add({
      targets: panel,
      x: GAME_WIDTH,
      duration: 180,
      ease: 'Sine.easeIn',
      onComplete: () => panel.destroy(),
    });
    this.container = null;
    this.current = null;
    this.bus.emit('teach:dismissed', { term });
    this.bus.emit('term:unlocked', { term });
    const next = this.queue.shift();
    if (next) this.show(next);
  }

  private teardown(): void {
    this.bus.off('teach:requested', this.onTeachRequested);
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
      this.current = null;
    }
  }
}
